import React, { useState, useEffect } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { ResponsiveImage, Modal } from '../../components';

import ImageCarousel from './ImageCarousel/ImageCarousel';
import ActionBarMaybe from './ActionBarMaybe';

import css from './ListingPage.module.css';

const SectionHero = props => {
  const {
    title,
    listing,
    isOwnListing,
    editParams,
    handleViewPhotosClick,
    imageCarouselOpen,
    onImageCarouselClose,
    onManageDisableScrolling,
    noPayoutDetailsSetWithOwnListing,
    isIframe = false,
  } = props;

  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [fetchingSignedUrls, setFetchingSignedUrls] = useState(false);

  const hasImages = listing.images && listing.images.length > 0;
  const firstImage = hasImages ? listing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith('scaled'))
    : [];
  const videos = listing.attributes.publicData.videos || [];

  // Prepare media array: videos first, then images
  const media = [
    ...uploadedVideos.map(v => ({ type: 'video', url: v.url, filename: v.filename })),
    ...(listing.images || []).map(img => ({ type: 'image', image: img })),
  ];

  const mediaCount = media.length;

  useEffect(() => {
    if (videos.length > 0 && !fetchingSignedUrls) {
      setFetchingSignedUrls(true);
      const videosWithoutUrl = videos.filter(video => !uploadedVideos.some(v => v.filename === video));

      // Use CloudFront URL for each video that doesn't have url
      videosWithoutUrl.forEach(video => {
        if (video) {
          const cloudfrontUrl = `${process.env.REACT_APP_CLOUDFRONT_URL}/${video}`;
          setUploadedVideos(prevState => [...prevState, { filename: video, url: cloudfrontUrl }]);
        }
      });
      setFetchingSignedUrls(false);
    }
  }, [videos, uploadedVideos]);

  const viewPhotosButton = (hasImages || videos.length > 0) ? (
    <button className={css.viewPhotos} onClick={handleViewPhotosClick}>
      <FormattedMessage
        id="ListingPage.viewImagesButton"
        values={{ count: listing.images.length + videos.length }}
      />
    </button>
  ) : null;

  const renderMedia = (item, idx, isHero = false) => {
    if (!item) return <div className={css.galleryPlaceholder}>Gallery</div>;
    if (item.type === 'video') {
      return (
        <video
          className={isHero ? css.heroVideo : css.galleryImage}
          controls={isHero}
          loop
          muted
          playsInline
          autoPlay={isHero}
          poster={isHero && listing.images && listing.images[0] ? listing.images[0].attributes.variants['scaled-large']?.url : undefined}
        >
          <source src={item.url} type="video/mp4" />
        </video>
      );
    }
    return (
      <ResponsiveImage
        rootClassName={isHero ? css.rootForImage : css.galleryImage}
        alt={`Gallery image ${idx + 1}`}
        image={item.image}
        variants={variants}
      />
    );
  };

  let layout;
  if (mediaCount === 1) {
    // Full size
    layout = (
      <div className={css.heroSingle} onClick={handleViewPhotosClick}>
        {renderMedia(media[0], 0, true)}
        {viewPhotosButton}
      </div>
    );
  } else if (mediaCount === 2) {
    // 50/50 split
    layout = (
      <div className={css.heroGridTwo}>
        <div className={css.heroHalf} onClick={handleViewPhotosClick}>
          {renderMedia(media[0], 0, true)}
        </div>
        <div className={css.heroHalf} onClick={handleViewPhotosClick}>
          {renderMedia(media[1], 1, true)}
        </div>
        {viewPhotosButton}
      </div>
    );
  } else if (mediaCount === 3 || mediaCount === 4) {
    // 1 half, 2x1 grid
    layout = (
      <div className={css.heroGridThreeFour}>
        <div className={css.heroMain} onClick={handleViewPhotosClick}>
          {renderMedia(media[0], 0, true)}
        </div>
        <div className={css.galleryGrid2x1} onClick={handleViewPhotosClick}>
          {[1, 2, 3].slice(0, mediaCount - 1).map(idx => (
            <div className={css.galleryItem} key={idx}>
              {renderMedia(media[idx], idx)}
            </div>
          ))}
        </div>
        {viewPhotosButton}
      </div>
    );
  } else {
    // 1 half, 2x2 grid
    layout = (
      <div className={css.heroGrid}>
        <div className={css.heroMain} onClick={handleViewPhotosClick}>
          {renderMedia(media[0], 0, true)}
        </div>
        <div className={css.galleryGrid} onClick={handleViewPhotosClick}>
          {[1, 2, 3, 4].map(idx => (
            <div className={css.galleryItem} key={idx}>
              {renderMedia(media[idx], idx)}
            </div>
          ))}
        </div>
        {viewPhotosButton}
      </div>
    );
  }

  return (
    <div className={css.sectionHero} data-testid="hero">
      {layout}
      {mediaCount > 1 && (
        <div className={css.mobileThumbGallery}>
          {media.slice(1).map((item, idx) => (
            <div className={css.mobileThumbItem} key={idx}>
              {item.type === 'video' ? (
                <video className={css.mobileThumbImage} muted playsInline>
                  <source src={item.url} type="video/mp4" />
                </video>
              ) : (
                <ResponsiveImage
                  rootClassName={css.mobileThumbImage}
                  alt={`Thumbnail ${idx + 2}`}
                  image={item.image}
                  variants={variants}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <Modal
        id="ListingPage.imageCarousel"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={imageCarouselOpen}
        onClose={onImageCarouselClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel
          images={listing.images}
          videos={uploadedVideos}
          imageVariants={['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge']}
        />
      </Modal>
    </div>
  );
};

export default SectionHero;
